<template>
  <appointment-management
    ref="AppointmentManagement"
    :academic-years="academicYears"
    :can-show-confirm-appointment="false"
    :can-show-denied-appointment="false"
    :can-show-recoordinate-appointment="false"
    :can-show-service-filter="canShowServiceFilter"
    :can-show-write-evaluation-action="canShowWriteEvaluationAction"
    :cancel-appointment="cancelAppointment"
    :change-center-appointment="changeCenterAppointment"
    :companies="companiesData"
    :deny-appointment="denyAppointment"
    :full-profile="fullProfileData"
    :get-academic-years="getAcademicYears"
    :get-appointment-filters="getAppointmentFilters"
    :get-appointments="getAppointments"
    :get-appointments-specialists="getAppointmentsSpecialists"
    :get-centers="getCenters"
    :get-discipline-types="getDisciplineTypes"
    :get-districts="getDistricts"
    :get-municipalities="getMunicipalities"
    :get-reasons-appointment-status="getReasonsApointmentStatus"
    :get-reasons-appointment-status-detail="getReasonsApointmentStatusDetail"
    :get-schools="getSchools"
    :get-service-locations="getServiceLocations"
    :get-type-places="getTypePlaces"
    :has-evaluation-document="hasEvaluationDocument"
    :has-permissions="hasPermissions"
    :is-provider="isProvider"
    :is-specialist="isSpecialist"
    :is-specialist-depr="isSpecialistDPER"
    :need-reason-detail="true"
    :on-download-evaluation-document="onDownloadEvaluationDocument"
    :on-download-pei="onDownloadPei"
    :on-download-referral-sheet="onDownloadReferralSheet"
    :on-get-additional-info="getAppointmentAdditionalInfo"
    :on-get-contact-information="getContactInformation"
    :on-get-therapies="getAppointmentTherapies"
    :on-replace-evaluation-document="onReplaceEvaluationDocument"
    :on-set-as-attended="setAsAttended"
    :show-view-record="allowedGoToStudentServiceRecord"
    @complete-evaluation="seeEvaluation"
    @redirect-view-record="handleRedirectViewAppointmentRecord"
    @see-evaluation="seeEvaluation"
  />
</template>

<script>
import { AppointmentManagement } from 'appointment-management';
import {
  getAppointmentFilters as _getAppointmentFilters,
  getAppointments as _getAppointments,
} from '@/modules/provider/api';
import { getSchools as _getSchools } from '@/modules/common/location.api';
import _academicYears from '@/modules/common/academicyear.api';
import { getAppointmentsSpecialists as _getAppointmentsSpecialists } from '@/modules/shared/provider.api';
import {
  getCenters as _getCenters,
  getDistricts as _getDistricts,
  getMunicipalities as _getMunicipalities,
} from '@/modules/shared/location.api';
import {
  getTypePlaces as _getTypePlaces,
  getServices as _getService,
} from '@/modules/shared/code.api';
import { getAcademicYears as _getAcademicYears } from '@/modules/shared/academicyear.api';
import {
  getReasonsApointmentStatus as _getReasonsApointmentStatus,
  getReasonsApointmentStatusDetail as _getReasonsApointmentStatusDetail,
  cancelAppointment as _cancelAppointment,
  denyAppointment as _denyAppointment,
  changeCenterAppointment as _changeCenterAppointment,
  getEvaluationDocument as _getEvaluationDocument,
  setAsAttended as _setAsAttended,
} from '@/modules/provider/api';

import {
  getContactInformation as _getContactInformation,
  getAppointmentAdditionalInfo as _getAppointmentAdditionalInfo,
  getAppointmentTherapies as _getAppointmentTherapies,
} from '@/modules/student/api';

import {
  getReferralsheetevaluation as _referralsheetevaluation,
  replaceSheetEvaluation as _replaceSheetEvaluation,
  getLatestSignedPeipReport as _getLatestSignedPeipReport,
} from '@/modules/shared/studentreferral.api';
import _generalApi from '@/modules/app/api';
import { getList as _getServiceLocations } from '@/modules/shared/servicelocation.api';
import { getSysparameterByNames as _getSysparameterByNames } from '@/modules/shared/sysparameter.api';

import roles from '@/utils/constants/roles.js';

import { mapGetters } from 'vuex';
import alertMixin from '@/mixins/alert.mixin.js';
import disciplineTypeCode from '@/utils/constants/disciplineTypeCode';
const DEPR = '472';
export default {
  name: 'AppointmentManagementWrapper',
  components: {
    AppointmentManagement,
  },
  mixins: [alertMixin],
  data() {
    return {
      allowedGoToStudentServiceRecord: false,
      writeEvaluationActionParam: false,
    };
  },
  computed: {
    ...mapGetters('$_user', ['fullProfile', 'companies']),
    academicYears() {
      return _academicYears;
    },
    fullProfileData() {
      return this.fullProfile;
    },
    companiesData() {
      return this.companies;
    },
    isProvider() {
      return [roles.Provider, roles.AsistenteProveedor].includes(this.userRole);
    },
    isSpecialist() {
      return this.fullProfile?.specialistId != null;
    },
    isSpecialistDPER() {
      return this.fullProfile.companyid
        ? this.fullProfile.companyid.includes(DEPR)
        : false;
    },
    isAdmin() {
      return [
        roles.Administrador,
        roles.AdminProvider,
        roles.AdminSAEE,
        roles.AdminIL,
        roles.UMESRAdmin,
      ].includes(this.fullProfile?.role);
    },
    canShowServiceFilter() {
      return this.isProvider || this.isSpecialist || this.isAdmin;
    },
    canShowWriteEvaluationAction() {
      return this.writeEvaluationActionParam;
    },
  },
  async mounted() {
    await this.getAllowedGoToStudentServiceRecordSysParam();
    await this.getAppointmentWriteEvaluationActionParam();
  },
  destroyed() {
    this.persistData();
  },
  methods: {
    seeEvaluation(appointment) {
      this.$router.push({
        name: 'vocationalEvaluation',
        params: {
          appointmentId: appointment.clientAppointmentId,
          id: appointment.vocationalEvaluationId ?? undefined,
        },
      });
    },
    hasPermissions(permission) {
      return this.userHasPermissions(permission);
    },
    getAppointmentFilters() {
      return _getAppointmentFilters();
    },
    getAppointments(filterProperties) {
      return _getAppointments(filterProperties);
    },
    async getDistricts(model) {
      var { data: districts } = await _getDistricts({
        AcademicYearId: model.AcademicYearId,
        CenterId: model.CenterId,
        ...{
          SkipUserMunicipalitiesFlag: true,
          SkipUserCentersFlag: true,
          SkipUserDistrictsFlag: true,
          SkipUserRegionsFlag: true,
          SkipUserSchoolCodesFlag: true,
          SkipUserSchoolsFlag: true,
        },
      });
      return districts;
    },
    getSchools(data) {
      return _getSchools({
        AcademicYearId: data.AcademicYearId,
        MunicipalityId: data.MunicipalityId,
        ...{
          SkipUserMunicipalitiesFlag: true,
          SkipUserCentersFlag: true,
          SkipUserDistrictsFlag: true,
          SkipUserRegionsFlag: true,
          SkipUserSchoolCodesFlag: true,
          SkipUserSchoolsFlag: true,
        },
      });
    },
    getAppointmentsSpecialists(properties) {
      return _getAppointmentsSpecialists(properties);
    },
    getCenters(filters) {
      let data = {
        ...filters,
      };
      if (this.isSpecialist) {
        data = {
          ...data,
          skipUserCentersFlag: true,
          skipUserRegionsFlag: true,
          skipUserDistrictsFlag: true,
          skipUserMunicipalitiesFlag: true,
          skipUserSchoolCodesFlag: true,
          skipUserSchoolsFlag: true,
        };
      }
      return _getCenters(data);
    },
    getMunicipalities(data) {
      return _getMunicipalities({
        AcademicYearId: data.AcademicYearId,
        CenterId: data.CenterId,
        ...{
          SkipUserMunicipalitiesFlag: true,
          SkipUserCentersFlag: true,
          SkipUserDistrictsFlag: true,
          SkipUserRegionsFlag: true,
          SkipUserSchoolCodesFlag: true,
          SkipUserSchoolsFlag: true,
        },
      });
    },
    getTypePlaces(id) {
      return _getTypePlaces(id);
    },
    getDisciplineTypes(serviceTypeId, validForFlag) {
      return _getService([serviceTypeId], [validForFlag], null, [
        disciplineTypeCode.AT,
      ]);
    },
    getAcademicYears() {
      return _getAcademicYears();
    },
    getReasonsApointmentStatus(filter) {
      return _getReasonsApointmentStatus(filter);
    },
    getReasonsApointmentStatusDetail(ReasonsApointmentStatusCd) {
      return _getReasonsApointmentStatusDetail(ReasonsApointmentStatusCd);
    },
    getServiceLocations(companyId) {
      return _getServiceLocations(companyId);
    },
    cancelAppointment(model) {
      return _cancelAppointment(model);
    },
    denyAppointment(model) {
      return _denyAppointment(model);
    },
    changeCenterAppointment(clientAppointmentId, centerId) {
      return _changeCenterAppointment(clientAppointmentId, centerId);
    },
    async onDownloadReferralSheet(row) {
      if (row.studentReferralId) {
        await _referralsheetevaluation(row.studentReferralId).then(
          (response) => {
            _generalApi.ConvertToBlob(response.data);
          }
        );
      }
    },
    onReplaceEvaluationDocument(params, file) {
      return _replaceSheetEvaluation(params, file);
    },
    async onDownloadPei(row) {
      if (row.client_SieId) {
        await _getLatestSignedPeipReport(row.client_SieId).then((res) => {
          _generalApi.documentDownloadReport(
            res.data.content,
            res.data.filename
          );
        });
      }
    },
    async onDownloadEvaluationDocument(row) {
      let clientAppointmentId = parseInt(
        row.formatedClientAppointmentId.replace('-', '')
      );

      await _getEvaluationDocument(clientAppointmentId)
        .then((response) => {
          _generalApi.ConvertToBlob(response.data[0]);
        })
        .catch((error) => {
          let errorMessage =
            error.response && error.response.data
              ? error.response.data.message
              : 'ha ocurrido un error inesperado.';
          this.ShowToast('Error', errorMessage, 'error');
        });
    },
    async hasEvaluationDocument(row) {
      let clientAppointmentId = parseInt(
        row.formatedClientAppointmentId.replace('-', '')
      );

      await _getEvaluationDocument(clientAppointmentId)
        .then((response) => {
          return response.data != null;
        })
        .catch(() => {
          return false;
        });
    },
    async getContactInformation(studentSieId) {
      return await _getContactInformation(studentSieId);
    },
    async setAsAttended(clientAppointmentId) {
      return await _setAsAttended(clientAppointmentId);
    },
    async getAppointmentAdditionalInfo(clientAppointmentId) {
      return await _getAppointmentAdditionalInfo(clientAppointmentId);
    },
    async getAppointmentTherapies(clientAppointmentId) {
      return await _getAppointmentTherapies(clientAppointmentId);
    },
    async getAllowedGoToStudentServiceRecordSysParam() {
      const { data } = await _getSysparameterByNames({
        parameterNames: ['AllowedGoToStudentServiceRecord'],
      });
      this.allowedGoToStudentServiceRecord =
        data?.length > 0 && data[0].staticValue?.toLowerCase() == 'true';
    },
    async getAppointmentWriteEvaluationActionParam() {
      const { data } = await _getSysparameterByNames({
        parameterNames: ['AppointmentWriteEvaluationActionEnabled'],
      });
      this.writeEvaluationActionParam =
        data?.length > 0 && data[0].staticValue?.toLowerCase() === 'true';
    },
    handleRedirectViewAppointmentRecord({ clientAppointmentId }) {
      if (!clientAppointmentId) return;
      this.$router.push({
        name: 'AppointmentRecord',
        params: { clientAppointmentId },
      });
    },
  },
  beforeRouteEnter(_, from, next) {
    const persistRoutes = ['provider'];
    next(async (vm) => {
      if (persistRoutes.some((x) => x == from.name)) {
        vm.shouldRecovery = false;
        vm.$refs['AppointmentManagement'].shouldRecovery = false;
      } else {
        vm.$refs['AppointmentManagement'].shouldRecovery = true;
        vm.$refs['AppointmentManagement'].recoveryData();
      }
    });
  },
};
</script>
